import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query";
import usersApi from "api/landlord/renters";
import moment from "moment";
import addressApi from "api/landlord/address";
import { useAuthContext } from "providers/Auth";
import { Layout } from "providers/Layout";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Button,
  ButtonSize,
  ButtonVariant,
  Checkbox,
  CreatableSelect,
  DateInput
} from "shared/components";
import { BaseInput } from "shared/components/forms/BaseInput";
import {
  moneyCentsParser,
  ssnParser,
  tenDigitTelParser
} from "shared/components/forms/BaseInput/parsers";
import { BreadCrumbs } from "shared/components/BreadCrumbs";
import { useFormTyped } from "shared/hooks";
import { Settings02, Upload01 } from "untitledui-js-base";
import { Row } from "./components/Row";
import { getErrorMessage } from "utils/error";
import { getSourceLabel } from "utils/getSourceLabel";
import { useDebouncedValue } from "shared/hooks/use-debounced-value";
import { getPropertyNameWithLongAddress } from "shared/addressUtils";
import { SubscribeModal } from "views/renters/components/Header/SubscribeModal";
import { loadBoomReportUnitOptions } from "api/landlord/asyncUnitProperties";
import { Property } from "types/landlord/address";
import { enrollmentsNewValidations } from "./validations";
import { toast } from "react-toastify";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import { Redirect } from "shared/components/Redirect";
import { isManualEnrollmentDisabledForPms } from "utils/manualEnrollmentRestrictions";
import { CreatableAsyncSelect } from "shared/components/AsyncSelect/CreatableAsyncSelect";
import { AddUnitDrawer } from "shared/app/AddUnits/drawer";
import { AddPropertyDrawer } from "shared/app/AddProperty/drawer";
import { getRenterEnrollmentType } from "views/renters/utils";

function capitalize(s: string) {
  if (s.length === 0) return s;
  if (s.length === 1) return s.toUpperCase();

  return s[0].toUpperCase() + s.slice(1).toLocaleLowerCase();
}

export const AddNewSubscriber = () => {
  const {
    Unit: dictionaryUnit,
    unit: dictionaryUnitLowercase,
    units: dictionaryUnits,
    property: dictionaryPropertyLowercase,
    Property
  } = useUnitsPropertiesDictionary();
  const [showEnrollConfirmationModal, setShowEnrollConfirmationModal] =
    useState(false);

  const [property, setProperty] = useState<Property | undefined>();
  const [newPropertyName, setNewPropertyName] = useState("");
  const [showAddProperty, setShowAddProperty] = useState(false);

  const [newUnitName, setNewUnitName] = useState("");
  const [showAddUnits, setShowAddUnits] = useState(false);
  const [unitCacheUniq, setUnitCacheUniq] = useState(0);

  const [propertySearchQuery, setPropertySearchQuery] = useState("");
  const [debouncedPropertySearch] = useDebouncedValue(propertySearchQuery, 500);

  const navigate = useNavigate();

  const { type, partner, partnerDefaultEnrollments, permissions } =
    useAuthContext();
  const isCreatable = type.isManual || type.isBoomOps;

  const { register, values, handleChange, onSubmit, isSubmitting } =
    useFormTyped(
      {
        firstName: "",
        lastName: "",
        dob: undefined,
        ssn: "",
        email: "",
        phone: "",
        unit: undefined,
        moveInDate: undefined,
        leaseEndDate: undefined,
        isMonthToMonth: false,
        // Cents, kept as a string so the yup schema stays string-based.
        currentRentAmount: "",
        optOut: true,
        address: ""
      },
      enrollmentsNewValidations
    );

  const {
    data: propertiesData,
    isLoading,
    refetch
  } = useQuery({
    queryKey: ["PROPERTIES", { property: debouncedPropertySearch }],
    queryFn: () =>
      addressApi
        .getProperties({
          per_page: 40,
          searchTerm: debouncedPropertySearch
        })
        .then((data: { items: Property[] }) => data.items),
    placeholderData: keepPreviousData
  });

  const { mutate: enrollRenter, isPending } = useMutation({
    mutationKey: ["ENROLL_RENTER"],
    mutationFn: (enrollmentOption: {
      optIn: boolean;
      optOut: boolean;
      isDefaultEnrollment: boolean;
    }) => {
      return usersApi.addRenter({
        property_id: property?.id,
        email: values.email,
        first_name: values.firstName,
        last_name: values.lastName,
        phone_number: values.phone,
        dob:
          values.dob && moment(values.dob).isValid()
            ? moment(values.dob).format("YYYY-MM-DD")
            : undefined,
        ssn: values.ssn,
        move_in_date: values.moveInDate,
        lease_end_date: values.isMonthToMonth ? undefined : values.leaseEndDate,
        // moneyCentsParser stores cents; the endpoint wants dollars.
        monthly_rent_amount: parseFloat(values.currentRentAmount) / 100,
        address1: property?.address.address1,
        city: property?.address.city,
        state_or_province: property?.address.state_or_province,
        postal_code: property?.address.postal_code,
        country: property?.address.country,
        lease_month_to_month: values.isMonthToMonth || undefined,
        no_communication: false,
        opt_in: enrollmentOption.optIn,
        opt_out: enrollmentOption.optOut,
        is_default_enrollment: enrollmentOption.isDefaultEnrollment,
        unit_id: values.unit
      });
    },
    onSuccess: () => {
      navigate("/rent-reporting");
      setShowEnrollConfirmationModal(false);
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    }
  });

  const isSubmitBlocked = Boolean(
    isSubmitting || isLoading || isPending || showEnrollConfirmationModal
  );

  if (
    (type.isPMS && !type.isBoomOps && !type.canManageEntitiesDirectly) ||
    type.isSDK ||
    !permissions.canSubscribeRenters ||
    isManualEnrollmentDisabledForPms(partner?.property_management_system?.name)
  )
    return <Redirect href="/" />;

  return (
    <Layout
      headerClassName="pb-0 pt-3"
      header={
        <div className="border-b border-alpha-100">
          <div className="mb-3 ml-6">
            <BreadCrumbs
              crumbs={[
                {
                  title: "Rent reporting",
                  href: "/rent-reporting"
                },
                {
                  title: "Add new subscriber",
                  href: "/rent-reporting/add"
                }
              ]}
            />
          </div>
          {(type.isManual ||
            (type.isBoomOps && type.isPMS) ||
            (type.isPMS && type.canManageEntitiesDirectly)) &&
          !isManualEnrollmentDisabledForPms(
            partner?.property_management_system?.name
          ) ? (
            <div className="flex items-center border-t border-alpha-100 gap-4 bg-primary-20 px-6 py-4">
              <p className="text-sm text-primary-500">
                Need to add multiple subscribers? Try our Import Data feature
              </p>
              <Link to="/rent-reporting/upload">
                <Button
                  Icon={Upload01}
                  size={ButtonSize.XS}
                  variant={ButtonVariant.BASE}
                >
                  Import data
                </Button>
              </Link>
            </div>
          ) : null}
        </div>
      }
      footer={
        <div className="flex justify-end gap-2">
          <Link to="/rent-reporting">
            <Button size={ButtonSize.BASE} variant={ButtonVariant.BASE}>
              Cancel
            </Button>
          </Link>
          <Button
            size={ButtonSize.BASE}
            variant={ButtonVariant.PRIMARY}
            form="single-upload-form"
            type="submit"
            loading={isSubmitBlocked}
            disabled={isSubmitBlocked}
          >
            Add Subscriber
          </Button>
        </div>
      }
    >
      <form
        id="single-upload-form"
        className="animate-appear mx-auto max-w-[760px] pb-4"
        onSubmit={onSubmit({
          onSuccess: () => {
            if (!property) {
              toast.error(`${Property} not found`);
              return;
            }

            setShowEnrollConfirmationModal(true);
          }
        })}
      >
        <div>
          <h2 className="text-base font-bold text-content-700">
            Personal Information
          </h2>
          <Row
            left={
              <BaseInput
                id="firstName"
                {...register("firstName", (d) => capitalize(String(d || "")))}
                type="text"
                hideOptional
                maxLength={20}
                label="Legal first name"
                placeholder="Legal first name"
              />
            }
            right={
              <BaseInput
                id="lastName"
                {...register("lastName", (d) => capitalize(String(d || "")))}
                type="text"
                hideOptional
                maxLength={20}
                label="Legal last name"
                placeholder="Legal last name"
              />
            }
          />

          <Row
            left={
              <BaseInput
                id="email"
                {...register("email")}
                hideOptional
                label="Email address"
                placeholder="example@gmail.com"
                type="email"
              />
            }
            right={
              <BaseInput
                id="phone"
                hideOptional
                {...register("phone")}
                parser={tenDigitTelParser({ asString: true })}
                label="Phone number"
                placeholder="(123) 000-000"
              />
            }
          />
        </div>

        <div className="my-6 h-px bg-alpha-80" />

        <div className="mt-6">
          <h2 className="text-base font-bold text-content-700">
            Identity verification
          </h2>

          <p className="my-4 text-sm font-medium text-content-500">
            Please provide at least one of the following:
          </p>

          <Row
            right={
              <BaseInput
                id="ssn"
                {...register("ssn")}
                parser={ssnParser({ asString: true })}
                hideOptional
                placeholder="XXX-232-3232"
                label="SSN"
              />
            }
            left={
              <DateInput
                id="dob"
                {...register("dob")}
                label="Date of birth"
                hideOptional
                placeholder="MM/DD/YYYY"
                max={Number(moment().subtract(18, "years").toDate())}
                // min={Number(moment().subtract(110, "years").toDate())}
              />
            }
          />

          <div className="mt-2 flex items-center gap-4 rounded-lg border border-alpha-100 bg-success-50 p-4">
            <Settings02 size="20px" className="stroke-success-700" />
            <p className="text-sm font-medium text-success-800">
              Identity verification will be done automatically
            </p>
          </div>
        </div>

        <div className="my-6 h-px bg-alpha-80" />

        <div className="mt-6">
          <h2 className="text-base font-bold text-content-700">
            Lease details
          </h2>
          <Row
            left={
              <CreatableSelect
                key="property-select"
                id="address"
                {...register("address", (d) => {
                  const propertyId = String(d ?? "");
                  setProperty(
                    propertiesData?.find((p) => String(p.id) === propertyId)
                  );
                  handleChange("unit", () => undefined)();
                  setUnitCacheUniq((uniq) => uniq + 1);

                  return propertyId;
                })}
                searchable
                label={Property}
                className="flex-1"
                loading={isLoading}
                isValidNewOption={() => isCreatable}
                onInputChange={(query) => setPropertySearchQuery(query)}
                handleCreate={(inputValue) => {
                  setNewPropertyName(inputValue);
                  setShowAddProperty(true);
                }}
                placeholder={
                  isCreatable
                    ? `Select or type to create a new ${dictionaryPropertyLowercase}`
                    : `Select or type a ${dictionaryPropertyLowercase}`
                }
                createOptionPosition="first"
                formatCreateLabel={(inputValue: string) => {
                  const title = inputValue.length
                    ? `${dictionaryPropertyLowercase} "${inputValue}"`
                    : `new ${dictionaryPropertyLowercase}`;

                  return (
                    <span className="text-primary-500">{`+ Add ${title}`}</span>
                  );
                }}
                options={[
                  ...(propertiesData?.map((property: Property) => {
                    return {
                      value: String(property.id),
                      label: getPropertyNameWithLongAddress(
                        property,
                        property.address
                      ),
                      sourceLabel: getSourceLabel(property)
                    };
                  }) || [])
                ]}
              />
            }
            right={
              <CreatableAsyncSelect
                key={`unit-select-${property?.id}-${unitCacheUniq}`}
                id="unit"
                label={dictionaryUnit}
                searchable={Boolean(property?.id)}
                hideOptional
                value={values.unit}
                onChange={(event) => {
                  const selected = event as unknown as {
                    target?: { value?: string };
                  };
                  handleChange("unit", () => selected?.target?.value)();
                }}
                isValidNewOption={() => property?.id && isCreatable}
                // @ts-expect-error
                loadOptions={loadBoomReportUnitOptions}
                cacheUniqs={[unitCacheUniq]}
                additional={{ page: 1, propertyId: property?.id || 0 }}
                handleCreate={(inputValue: string) => {
                  setNewUnitName(inputValue);
                  setShowAddUnits(true);
                }}
                defaultOptions
                placeholder={
                  isCreatable
                    ? `Select or type to create a new ${dictionaryUnitLowercase}`
                    : `Select or type a ${dictionaryUnitLowercase}`
                }
                createOptionPosition="first"
                formatCreateLabel={(inputValue: string) => {
                  const title = inputValue.length
                    ? `${dictionaryUnitLowercase} "${inputValue}"`
                    : `new ${dictionaryUnits}`;

                  return (
                    <span className="text-primary-500">{`+ Add ${title}`}</span>
                  );
                }}
              />
            }
          />
          <Row
            left={
              <DateInput
                id="moveInDate"
                {...register("moveInDate")}
                placeholder="MM/DD/YYYY"
                label="Move-in date"
                hideOptional
              />
            }
            right={
              <div className="mb-4 flex-1 gap-6">
                <DateInput
                  id="leaseEndDate"
                  {...register("leaseEndDate")}
                  placeholder="MM/DD/YYYY"
                  label="Lease end date"
                  disabled={values.isMonthToMonth}
                  readOnly={values.isMonthToMonth}
                  hideOptional
                />
                <Checkbox
                  id="isMonthToMonth"
                  {...register("isMonthToMonth")}
                  checked={values.isMonthToMonth}
                  label="It's month to month / no end date"
                />
              </div>
            }
          />

          <div className="money-input-2 w-1/2 pr-4">
            <BaseInput
              id="currentRentAmount"
              {...register("currentRentAmount")}
              parser={moneyCentsParser({ asString: true })}
              hideOptional
              prefix="$"
              label="Rent amount"
            />
          </div>
        </div>
      </form>
      <SubscribeModal
        isOpen={showEnrollConfirmationModal}
        setIsOpen={(opened) => {
          setShowEnrollConfirmationModal(opened);
        }}
        renterName={`${values.firstName} ${values.lastName}`}
        enrollRenters={enrollRenter}
        defaultSelectedEnrollmentType={
          partnerDefaultEnrollments?.default_enrollment_type ||
          partnerDefaultEnrollments?.state_province_defaults.length
            ? "isDefaultEnrollment"
            : undefined
        }
        rentersStateCounts={getRenterEnrollmentType({
          state: property?.address.state_or_province || "",
          address1: property?.address.address1,
          propertyName: property?.name,
          defaultEnrollmentTypes:
            partnerDefaultEnrollments?.state_province_defaults || [],
          partnerDefaultEnrollmentType:
            partnerDefaultEnrollments?.default_enrollment_type,
          propertyDefaults: partnerDefaultEnrollments?.property_defaults ?? []
        })}
      />
      <AddPropertyDrawer
        open={showAddProperty}
        newPropertyName={newPropertyName}
        onClose={() => setShowAddProperty(false)}
        onSuccess={(newProperty) => {
          refetch().then(async () => {
            if (newProperty) {
              setNewPropertyName("");
              setProperty(newProperty);
              handleChange("address", () => String(newProperty.id))();
              handleChange("unit", () => undefined)();
              setUnitCacheUniq((uniq) => uniq + 1);
            }
          });
        }}
      />
      <AddUnitDrawer
        open={showAddUnits}
        selectedProperty={property}
        newUnitName={newUnitName}
        onClose={() => setShowAddUnits(false)}
        onSuccess={(newUnit) => {
          setNewUnitName("");
          handleChange("unit", () => newUnit.id)();
          setUnitCacheUniq((uniq) => uniq + 1);
        }}
      />
    </Layout>
  );
};
